import type { RouteLocationNormalizedLoaded } from "vue-router";
import { nextTick } from "vue";
import { defineStore } from "pinia";

interface RouteCacheEntry {
  tabKey: string;
  leafRouteName: string;
  componentNames: string[];
  componentSignatures: Record<string, string>;
}

interface RouteCacheSource {
  matched: RouteLocationNormalizedLoaded["matched"];
}

export interface RouteCacheDefinition {
  name: string;
  signature: string;
  keepAlive: boolean;
}

interface RouteCacheState {
  entries: Record<string, RouteCacheEntry>;
  renderVersions: Record<string, number>;
  refreshingRouteNames: string[];
  invalidatingComponentNames: string[];
  retainedComponentNames: string[];
  scrollPositions: Record<string, number>;
}

function normalizeRouteName(name: RouteLocationNormalizedLoaded["name"]) {
  return typeof name === "string" ? name : undefined;
}

function getCacheComponentNames(route: RouteCacheSource) {
  return route.matched
    .slice(1)
    .filter((record) => Boolean(record.components?.default))
    .map((record) => normalizeRouteName(record.name))
    .filter((name): name is string => Boolean(name));
}

function getComponentSignature(routeName: string, value: unknown) {
  return `${routeName}:${typeof value === "string" ? value : "UNKNOWN_COMPONENT"}`;
}

export const useRouteCacheStore = defineStore("route-cache", {
  state: (): RouteCacheState => ({
    entries: {},
    renderVersions: {},
    refreshingRouteNames: [],
    invalidatingComponentNames: [],
    retainedComponentNames: [],
    scrollPositions: {},
  }),
  getters: {
    cachedComponentNames(state) {
      const suspendedNames = new Set([
        ...state.refreshingRouteNames,
        ...state.invalidatingComponentNames,
      ]);
      const names = new Set(state.retainedComponentNames);

      Object.values(state.entries).forEach((entry) => {
        entry.componentNames.forEach((name) => {
          if (!suspendedNames.has(name)) {
            names.add(name);
          }
        });
      });

      return [...names].filter((name) => !suspendedNames.has(name));
    },
    getRenderVersion: (state) => (routeName: string) => state.renderVersions[routeName] || 0,
    isRenderingSuspended: (state) => (routeName: string) =>
      state.refreshingRouteNames.includes(routeName) ||
      state.invalidatingComponentNames.includes(routeName),
    getScrollPosition: (state) => (tabKey: string) => state.scrollPositions[tabKey] || 0,
  },
  actions: {
    registerRoute(route: RouteCacheSource, tabKey: string) {
      const leafRecord = route.matched.at(-1);
      const leafRouteName = normalizeRouteName(leafRecord?.name);

      if (leafRecord?.meta.keepAlive !== true || !leafRouteName) {
        this.unregisterRoute(tabKey);
        return;
      }

      const componentNames = getCacheComponentNames(route);

      if (!componentNames.includes(leafRouteName)) {
        this.unregisterRoute(tabKey);
        return;
      }

      this.entries[tabKey] = {
        tabKey,
        leafRouteName,
        componentNames,
        componentSignatures: Object.fromEntries(
          route.matched.slice(1).flatMap((record) => {
            const routeName = normalizeRouteName(record.name);
            return routeName
              ? [[routeName, getComponentSignature(routeName, record.meta.routeComponentKey)]]
              : [];
          }),
        ),
      };
    },
    unregisterRoute(tabKey: string) {
      const entry = this.entries[tabKey];

      delete this.entries[tabKey];

      if (entry) {
        this.refreshingRouteNames = this.refreshingRouteNames.filter(
          (name) => name !== entry.leafRouteName,
        );
      }
    },
    removeTab(tabKey: string) {
      const entry = this.entries[tabKey];
      const routeName = entry?.leafRouteName || tabKey;
      const componentNames = entry?.componentNames || [routeName];

      delete this.entries[tabKey];
      delete this.scrollPositions[tabKey];
      const retainedNames = new Set(
        Object.values(this.entries).flatMap((cacheEntry) => cacheEntry.componentNames),
      );
      componentNames.forEach((name) => {
        if (!retainedNames.has(name)) {
          delete this.renderVersions[name];
        }
      });
      this.refreshingRouteNames = this.refreshingRouteNames.filter((name) => name !== routeName);
    },
    removeTabs(tabKeys: string[]) {
      tabKeys.forEach((tabKey) => this.removeTab(tabKey));
    },
    async retainRouteDefinitions(definitions: RouteCacheDefinition[]) {
      const definitionByName = new Map(
        definitions.map((definition) => [definition.name, definition]),
      );
      const validNames = new Set(definitionByName.keys());
      const invalidEntries: Array<{
        entry: RouteCacheEntry;
        changedComponentNames: string[];
      }> = [];

      Object.values(this.entries).forEach((entry) => {
        const leafDefinition = definitionByName.get(entry.leafRouteName);

        if (leafDefinition && !leafDefinition.keepAlive) {
          this.unregisterRoute(entry.tabKey);
          return;
        }

        const signaturesAreValid =
          leafDefinition?.keepAlive === true &&
          entry.componentNames.every(
            (name) =>
              validNames.has(name) &&
              entry.componentSignatures[name] === definitionByName.get(name)?.signature,
          );

        if (!signaturesAreValid) {
          invalidEntries.push({
            entry,
            changedComponentNames: entry.componentNames.filter(
              (name) =>
                !validNames.has(name) ||
                entry.componentSignatures[name] !== definitionByName.get(name)?.signature,
            ),
          });
        }
      });

      invalidEntries.forEach(({ entry, changedComponentNames }) => {
        delete this.scrollPositions[entry.tabKey];
        this.invalidatingComponentNames = [
          ...new Set([...this.invalidatingComponentNames, ...changedComponentNames]),
        ];
        this.retainedComponentNames = [
          ...new Set([
            ...this.retainedComponentNames,
            ...entry.componentNames.filter((name) => !changedComponentNames.includes(name)),
          ]),
        ];
      });

      if (invalidEntries.length > 0) {
        await nextTick();
      }

      invalidEntries.forEach(({ entry }) => {
        delete this.entries[entry.tabKey];
      });

      const changedRouteNames = new Set(
        invalidEntries.flatMap(({ changedComponentNames }) => changedComponentNames),
      );
      changedRouteNames.forEach((routeName) => {
        this.renderVersions[routeName] = (this.renderVersions[routeName] || 0) + 1;
      });

      Object.keys(this.renderVersions).forEach((routeName) => {
        if (!validNames.has(routeName)) {
          delete this.renderVersions[routeName];
        }
      });

      this.refreshingRouteNames = this.refreshingRouteNames.filter((name) => validNames.has(name));
    },
    completeRouteInvalidations() {
      this.invalidatingComponentNames = [];
      this.retainedComponentNames = [];
    },
    async refreshRoute(routeName: string, tabKey: string) {
      if (this.refreshingRouteNames.includes(routeName)) {
        return;
      }

      this.scrollPositions[tabKey] = 0;
      this.refreshingRouteNames.push(routeName);
      await nextTick();

      this.renderVersions[routeName] = (this.renderVersions[routeName] || 0) + 1;
      this.refreshingRouteNames = this.refreshingRouteNames.filter((name) => name !== routeName);
      await nextTick();
    },
    setScrollPosition(tabKey: string, scrollTop: number) {
      if (!Number.isFinite(scrollTop)) {
        return;
      }

      this.scrollPositions[tabKey] = Math.max(0, scrollTop);
    },
    clearAll() {
      this.entries = {};
      this.renderVersions = {};
      this.refreshingRouteNames = [];
      this.invalidatingComponentNames = [];
      this.retainedComponentNames = [];
      this.scrollPositions = {};
    },
  },
});
